/**
 * Bons plans — impression GA4 du bloc, et alignement des titres par rangee.
 *
 * Impression : pousser `view_item_list` quand le bloc est REELLEMENT VU.
 * Le compter au chargement gonflerait le denominateur du CTR avec des visiteurs qui
 * n'ont jamais atteint le bloc — c'est precisement la metrique qui justifie le
 * composant. Meme protocole que assets/velos-vus-recemment.js.
 *
 * Le verrou `__mintBonsPlansInit` protege du DOUBLE CHARGEMENT du fichier (rechargement
 * de section dans l'editeur, ou deux `<script src>` identiques), pas de deux instances
 * de section : le schema pose `"limit": 1` (finding F18). Il ne tue aucune init — la
 * premiere execution lie tous les `[data-bons-plans]` presents et enregistre l'unique
 * ecouteur `shopify:section:load`, qui rejoue `init` sur toute section re-rendue.
 *
 * L'impression ne part JAMAIS en contexte FMD sans test supplementaire : le bloc y
 * reste `hidden`, donc `display: none`, et un IntersectionObserver ne declenche pas sur
 * un element sans boite.
 *
 * `select_item` n'est PAS gere ici : le listener delegue de
 * snippets/tracking-ecommerce.liquid le prend deja par les classes de la carte, a
 * condition que le titre soit un `h2.section-header__title` dans le meme `.page-width`.
 * `click_cta` non plus : le delegue dedie vit dans tracking-ecommerce.liquid, parce que
 * tracking-ui-forms.liquid ecarte tout element portant `data-cta-name`.
 */
(function () {
  'use strict';

  if (window.__mintBonsPlansInit) return;
  window.__mintBonsPlansInit = true;

  function track(name, payload) {
    if (typeof window.trackEvent === 'function') {
      window.trackEvent(name, payload);
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: name }, payload));
  }

  function priceOf(card) {
    var el = card.querySelector('.grid-product__price-final');
    if (!el) return 0;
    var raw = (el.innerText || el.textContent || '').replace(/[^0-9,.]/g, '').replace(',', '.');
    var value = parseFloat(raw);
    return isNaN(value) ? 0 : value;
  }

  /** La marque est le premier noeud texte du titre, comme dans select_item. */
  function brandOf(card) {
    var title = card.querySelector('.grid-product__title');
    if (!title || !title.childNodes.length) return '';
    return (title.childNodes[0].textContent || '').trim();
  }

  function sendViewItemList(root) {
    if (root.dataset.bpViewSent === '1') return;

    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-product-id]'));
    var items = cards.map(function (card, i) {
      var title = card.querySelector('.grid-product__title');
      return {
        item_id: card.getAttribute('data-product-id') || '',
        item_name: title ? title.textContent.replace(/\s+/g, ' ').trim() : '',
        item_brand: brandOf(card),
        item_category: card.getAttribute('data-product-category') || '',
        item_variant: card.getAttribute('data-product-variant') || '',
        price: priceOf(card),
        index: i + 1,
      };
    });
    if (items.length === 0) return;

    root.dataset.bpViewSent = '1';

    // `item_list_name` doit valoir le TEXTE DU H2 : select_item ne porte pas
    // d'item_list_id, il porte `carrousel_name` = texte du h2. Sans cet alignement le
    // CTR de la liste ne se calcule pas.
    var heading = root.querySelector('.bons-plans__title');
    track('view_item_list', {
      ecommerce: {
        item_list_id: 'bons_plans',
        item_list_name: heading ? heading.textContent.trim() : 'bons_plans',
        items: items,
      },
    });
  }

  function observe(root) {
    if (typeof IntersectionObserver !== 'function') {
      sendViewItemList(root);
      return;
    }
    // Le declenchement ne doit PAS dependre de la hauteur du bloc. Un `threshold: 0.3`
    // (recopie de velos-vus-recemment.js, qui est un carrousel d'UNE rangee) exige 30 %
    // du bloc a l'ecran : a `limit: 12`, la grille mobile fait 6 rangees, 30 % depassent
    // le viewport d'un petit telephone et l'impression ne part jamais — soit exactement
    // la metrique qui justifie ce fichier. On declenche donc des que le haut du bloc
    // remonte au quart inferieur de l'ecran, quelle que soit sa hauteur.
    var io = new IntersectionObserver(
      function (entries) {
        if (!entries[0] || !entries[0].isIntersecting) return;
        sendViewItemList(root);
        io.disconnect();
      },
      { rootMargin: '0px 0px -25% 0px', threshold: 0 },
    );
    io.observe(root);
  }

  /**
   * Titres a la hauteur du plus haut de leur rangee : une marque ou un modele sur deux
   * lignes decalait toute la carte par rapport a sa voisine. Les rangees se lisent au
   * `offsetTop` des cartes, donc 2 par rangee en mobile comme 4 en desktop.
   */
  function alignTitles(root) {
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-bp-grid] .grid-product'));
    var titles = cards.map(function (card) {
      var title = card.querySelector('.grid-product__title');
      if (title) title.style.minHeight = '';
      return title;
    });
    var rows = {};
    cards.forEach(function (card, i) {
      if (!titles[i]) return;
      var top = card.offsetTop;
      (rows[top] = rows[top] || []).push(titles[i]);
    });
    Object.keys(rows).forEach(function (top) {
      var row = rows[top];
      if (row.length < 2) return;
      var max = Math.max.apply(
        null,
        row.map(function (t) {
          return t.getBoundingClientRect().height;
        }),
      );
      row.forEach(function (t) {
        t.style.minHeight = max + 'px';
      });
    });
  }

  function bindAlign(root) {
    var frame = 0;
    function schedule() {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(function () {
        alignTitles(root);
      });
    }
    schedule();
    window.addEventListener('resize', schedule);
    root.addEventListener('bons-plans:grid-changed', schedule);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-bons-plans]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.bpBound === '1') return;
      root.dataset.bpBound = '1';
      observe(root);
      bindAlign(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // L'editeur re-rend la section sans recharger la page : le JS de chargement ne se
  // rejoue pas tout seul (best-practices/editor/integrate-sections-and-blocks).
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
